import React, { ReactNode, useState } from 'react';

import { useAppDispatch } from '@/lib/hooks';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';

import { CourseId } from 'course-types';
import InfoModal from '@/common/components/modals/general/InfoModal';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';

interface CourseCardProps {
  title: string;
  id: CourseId;
  courseCode?: string;
  onClick?: () => void;
  onRemove?: () => void;
  participantCount?: number; // Displays total group member count (users access courses through groups)
  className?: string; // Optional extra classes for outer card container
  // TODO: Implement assignmentsToGrade - requires new/updated backend endpoint to retrieve incoming due dates for assignments
  // assignmentsToGrade?: number;
  // TODO: Implement updatedAt timestamp when backend supports it
  // updatedAt?: string;
}

const CourseCard: React.FC<CourseCardProps> = ({
  title,
  id,
  courseCode,
  onClick,
  participantCount, // Group member count
  className,
  // TODO: Re-enable these props once backend endpoints are implemented
  // assignmentsToGrade = 0,
  // updatedAt = 'Updated today',
}) => {
  const dispatch = useAppDispatch();

  const [resolvedCourseCode, setResolvedCourseCode] = useState<string | undefined>(courseCode);

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const modalName = `ShareCourse-${id}`;
    dispatch(setModalVisible(true));
    dispatch(setModalName(modalName));

    if (!resolvedCourseCode) {
      try {
        const courseData = await UserCourseStorageService.setCourseData(id);
        setResolvedCourseCode(courseData?.course_code?.code);
      } catch (error) {
        // Silent failure; InfoModal will continue to show fallback text.
        console.error('Failed to load course code for sharing:', error);
      }
    }
  };

  /**
   * Renders the participant count with icon (shows total group members)
   */
  const renderParticipantCount = (): ReactNode => {
    return (
      <div className='flex items-center gap-1.5 text-sm text-text_gray_600'>
        <svg
          width='20'
          height='20'
          viewBox='0 0 20 20'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <g clipPath='url(#clip0_1168_1995)'>
            <path
              d='M14.1668 17.5V15.8333C14.1668 14.9493 13.8156 14.1014 13.1905 13.4763C12.5654 12.8512 11.7176 12.5 10.8335 12.5H4.16683C3.28277 12.5 2.43493 12.8512 1.80981 13.4763C1.18469 14.1014 0.833496 14.9493 0.833496 15.8333V17.5M19.1668 17.5V15.8333C19.1663 15.0948 18.9205 14.3773 18.468 13.7936C18.0155 13.2099 17.3819 12.793 16.6668 12.6083M13.3335 2.60833C14.0505 2.79192 14.686 3.20892 15.1399 3.79359C15.5937 4.37827 15.84 5.09736 15.84 5.8375C15.84 6.57764 15.5937 7.29673 15.1399 7.88141C14.686 8.46608 14.0505 8.88308 13.3335 9.06667M10.8335 5.83333C10.8335 7.67428 9.34111 9.16667 7.50016 9.16667C5.65921 9.16667 4.16683 7.67428 4.16683 5.83333C4.16683 3.99238 5.65921 2.5 7.50016 2.5C9.34111 2.5 10.8335 3.99238 10.8335 5.83333Z'
              stroke='currentColor'
              strokeWidth='1.67'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </g>
          <defs>
            <clipPath id='clip0_1168_1995'>
              <rect width='20' height='20' fill='white' />
            </clipPath>
          </defs>
        </svg>
        <span className='font-medium'>{participantCount}</span>
      </div>
    );
  };

  /**
   * Renders the share icon button
   */
  const renderShareIcon = (): ReactNode => {
    return (
      <button
        onClick={handleShare}
        className='p-1 rounded hover:bg-surface_gray_100 transition-colors text-text_gray_600'
        title='Share course'
      >
        <svg
          width='20'
          height='20'
          viewBox='0 0 20 20'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M3.3335 9.99999V16.6667C3.3335 17.1087 3.50909 17.5326 3.82165 17.8452C4.13421 18.1577 4.55814 18.3333 5.00016 18.3333H15.0002C15.4422 18.3333 15.8661 18.1577 16.1787 17.8452C16.4912 17.5326 16.6668 17.1087 16.6668 16.6667V9.99999M13.3335 4.99999L10.0002 1.66666M10.0002 1.66666L6.66683 4.99999M10.0002 1.66666V12.5'
            stroke='currentColor'
            strokeWidth='1.66667'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>
    );
  };

  // TODO: Re-enable assignments badge once backend endpoint is available for retrieving due dates
  /**
   * Renders the assignments to grade badge
   */
  // const renderAssignmentsBadge = (): ReactNode => {
  //   return (
  //     <div
  //       className='flex items-center gap-2 px-3 py-1.5 rounded-full border whitespace-nowrap'
  //       style={{
  //         background: '#FEF0C7',
  //         borderColor: '#FEF0C7'
  //       }}
  //     >
  //       <svg
  //         className='w-4 h-4 flex-shrink-0'
  //         fill='none'
  //         stroke='#DC6803'
  //         strokeWidth={2}
  //         viewBox='0 0 24 24'
  //         xmlns='http://www.w3.org/2000/svg'
  //       >
  //         <circle cx='12' cy='12' r='9' strokeWidth={2.5} />
  //         <path d='M12 8v4' strokeLinecap='round' strokeWidth={2.5} />
  //         <circle cx='12' cy='16' r='1' fill='#DC6803' stroke='none' />
  //       </svg>
  //       <span className='text-xs font-medium' style={{ color: '#DC6803' }}>
  //         {assignmentsToGrade} assignment{assignmentsToGrade !== 1 ? 's' : ''} to be graded
  //       </span>
  //     </div>
  //   );
  // };

  /**
   * Renders the manage course button
   */
  const renderManageCourseButton = (): ReactNode => {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) onClick();
        }}
        className='flex justify-center items-center self-stretch opacity-100 gap-2 border px-3.5 py-2 rounded-lg border transition-all duration-200 bg-[color-mix(in_srgb,var(--color-brand-highlight)_13.6%,var(--surface-white))] border-[color-mix(in_srgb,var(--color-brand-highlight)_13.6%,var(--surface-white))] text-primary hover:bg-primary hover:text-text_contrast shadow-[0px_1px_2px_0px_#0A0D120D]'
      >
        <svg
          className='w-4 h-4'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
          />
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
          />
        </svg>
        <span className='text-inherit font-semibold text-sm leading-5 tracking-[0%]'>
          Manage course
        </span>
      </button>
    );
  };

  return (
    <>
      <div
        className={`flex flex-col w-full min-h-[172px] p-4 gap-2 rounded-2xl border border-border_gray_300 bg-surface_white transition-shadow shadow-[0px_2px_4px_-2px_#0A0D120F] shadow-[0px_4px_8px_-2px_#0A0D121A] hover:shadow-md ${
          className ?? ''
        }`}
      >
        {/* Header with participant count and share icon - only show if participantCount is provided */}
        {participantCount !== undefined ? (
          <div className='flex justify-between items-center w-full'>
            {renderParticipantCount()}
            {renderShareIcon()}
          </div>
        ) : (
          <div className='flex justify-end items-center w-full'>{renderShareIcon()}</div>
        )}

        {/* Course Title */}
        <h4 className='font-semibold text-sm text-text_gray_900 line-clamp-2'>{title}</h4>

        {/* TODO: Re-enable updated timestamp once backend supports it */}
        {/* {updatedAt && <p className='text-xs text-gray-500'>{updatedAt}</p>} */}

        {/* TODO: Re-enable assignments badge once backend endpoint is available for retrieving due dates */}
        {/* {assignmentsToGrade !== undefined && renderAssignmentsBadge()} */}

        {/* Spacer to push button to bottom */}
        <div className='flex-grow'></div>

        {/* Manage course button */}
        {renderManageCourseButton()}
      </div>

      {/* Share Course Code Modal */}
      <InfoModal
        modalName={`ShareCourse-${id}`}
        title={`Share "${title}"`}
        subtitle='Share your course code with learners'
        className='max-w-[500px] w-full'
        closeOnClickOff={false}
        copyFeature={{
          text: resolvedCourseCode || '',
          enabled: !!resolvedCourseCode,
        }}
      >
        <div className='flex flex-col gap-1'>
          <p className='text-sm text-text_gray_900'>Course Code</p>
          <p className='text-2xl font-semibold'>
            {resolvedCourseCode || 'No course code available'}
          </p>
        </div>
        <p className='text-sm text-text_gray_900 mt-2'>
          Share this code with learners to let them join this course.
        </p>
      </InfoModal>
    </>
  );
};

export default CourseCard;
